<template>
    <div
        class="tooltip relative inline-block cursor-pointer z-9"
        @mouseenter="handleOpen"
        @mouseleave="open = false"
    >
        <button
            class="block focus:outline-none focus:ring-0 active:ring-0 rounded"
            aria-haspopup="true"
            :aria-expanded="open"
            @focus="handleOpen"
            @focusout="open = false"
            @click.prevent
        >
            <slot name="trigger">
                <i :class="`fa-regular ${icon} ml-1`"></i>
            </slot>
        </button>
        <div
            ref="placementEl"
            class="z-40 absolute transform transition-all"
            :class="[
                placementClass,
                wrap && wrapWidthClass,
            ]"
        >
            <div :style="shiftStyle">
                <transition name="fadeInOut">
                    <div
                        class="inline-block px-2 py-1 rounded-lg shadow-sm bg-[#1E293B] text-white"
                        v-show="open"
                        v-cloak
                    >
                        <div
                            class="text-xs"
                            :class="
                                wrap
                                    ? 'whitespace-normal text-left leading-5'
                                    : 'whitespace-nowrap text-center'
                            "
                        >
                            <slot>{{ text }}</slot>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        text: String,
        // Placement props for horizontal and vertical positioning
        xPlacement: {
            type: String,
            default: 'center',
            validator: (value) => ['left', 'center', 'right'].includes(value),
        },
        yPlacement: {
            type: String,
            default: 'top',
            validator: (value) => ['top', 'bottom'].includes(value),
        },
        // Side placement overrides x/y when set: 'right' anchors the tooltip
        // to the trigger's right edge (centered vertically) and 'left' to
        // its left edge. Used by widget-header info icons that need the
        // tooltip beside, not above, the icon.
        placement: {
            type: String,
            default: null,
            validator: (value) => !value || ['right', 'left'].includes(value),
        },
        icon: {
            type: String,
            default: 'fa-circle-exclamation',
        },
        wrap: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        open: false,
        shiftX: 0,
    }),
    computed: {
        placementClass() {
            if (this.placement === 'right') {
                return 'left-full top-1/2 -translate-y-1/2 ml-2';
            }
            if (this.placement === 'left') {
                return 'right-full top-1/2 -translate-y-1/2 mr-2';
            }
            const x = {
                center: '-translate-x-1/2 left-1/2',
                left: 'right-0',
                right: 'left-0',
            }[this.xPlacement] || '';
            const y = {
                top: 'bottom-2/3 mb-2',
                bottom: 'top-full mt-2',
            }[this.yPlacement] || '';
            return `${x} ${y}`;
        },
        wrapWidthClass() {
            // On narrow viewports cap to viewport-minus-padding so the bubble
            // never paints past the screen edge; sm+ keeps the original cap.
            return 'w-max max-w-[calc(100vw-1rem)] sm:max-w-[388px]';
        },
        shiftStyle() {
            return this.shiftX ? { transform: `translateX(${this.shiftX}px)` } : null;
        },
    },
    directives: {},
    methods: {
        async handleOpen() {
            this.open = true;
            this.shiftX = 0;
            await this.$nextTick();
            this.clampToViewport();
        },
        clampToViewport() {
            const el = this.$refs.placementEl;
            if (!el || typeof window === 'undefined') return;
            const rect = el.getBoundingClientRect();
            const vw = window.innerWidth;
            const padding = 8;

            if (rect.left < padding) {
                this.shiftX = padding - rect.left;
            } else if (rect.right > vw - padding) {
                this.shiftX = vw - padding - rect.right;
            } else {
                this.shiftX = 0;
            }
        },
    },
    created() {},
    mounted() {},
};
</script>
<style lang="scss" scoped>
.fadeInOut-enter-active,
.fadeInOut-leave-active {
    transition: opacity 0.8s;
    opacity: 1;
}
.fadeInOut-enter,
.fadeInOut-leave-to {
    opacity: 0;
}
</style>
